import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles?: string[];
}

interface NavSection {
  label: string;
  roles?: string[];
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: "General",
    items: [
      {
        label: "Dashboard",
        path: "/",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="3" y="14" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "Properties",
    roles: ["super_admin", "property_manager"],
    items: [
      {
        label: "Properties",
        path: "/properties",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Meters",
        path: "/meters",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "People",
    roles: ["super_admin", "property_manager"],
    items: [
      {
        label: "Tenants",
        path: "/tenants",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "Finance",
    roles: ["super_admin", "property_manager"],
    items: [
      {
        label: "Payments",
        path: "/payments",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" strokeLinecap="round" />
            <path d="M6 15h4" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: "Credit Billing",
        path: "/credit-management",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 14v-4M10 12h4" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "Reports",
    roles: ["super_admin", "property_manager"],
    items: [
      {
        label: "Analytics",
        path: "/analytics",
        roles: ["super_admin", "property_manager"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M3 17l4-8 4 5 3-3 4 6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 21h18" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "My Water",
    roles: ["tenant"],
    items: [
      {
        label: "Meters",
        path: "/meters",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
      {
        label: "Buy Tokens",
        path: "/buy-tokens",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2z" />
            <path d="M12 6v2m0 8v2M8.5 8.5l1.4 1.4m4.2 4.2 1.4 1.4M6 12h2m8 0h2M8.5 15.5l1.4-1.4m4.2-4.2 1.4-1.4" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: "Payments",
        path: "/payments",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <rect x="2" y="5" width="20" height="14" rx="2" />
            <path d="M2 10h20" strokeLinecap="round" />
            <path d="M6 15h4" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "Billing",
    roles: ["tenant"],
    items: [
      {
        label: "Credit Billing",
        path: "/credit-billing",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 14v-4M10 12h4" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: "Auto Top-Up",
        path: "/auto-deductions",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M12 8v4l3 3" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="9" />
            <path d="M16.24 7.76A6 6 0 0 1 18 12" strokeLinecap="round" />
          </svg>
        ),
      },
      {
        label: "Recurring Billing",
        path: "/recurring-billing",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M1 4v6h6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M23 20v-6h-6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ),
      },
    ],
  },
  {
    label: "Insights",
    roles: ["tenant"],
    items: [
      {
        label: "My Analytics",
        path: "/my-analytics",
        roles: ["tenant"],
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[18px] h-[18px]">
            <path d="M3 17l4-8 4 5 3-3 4 6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M3 21h18" strokeLinecap="round" />
          </svg>
        ),
      },
    ],
  },
];

// flat list for legacy usage
const navItems: NavItem[] = navSections.flatMap((s) => s.items);

function NavLink({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  return (
    <Link
      to={item.path}
      onClick={onClick}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-medium transition-all duration-150 ${
        active
          ? "bg-white/15 text-white shadow-sm"
          : "text-blue-200/80 hover:bg-white/8 hover:text-white"
      }`}
    >
      <span
        className={`flex-shrink-0 transition-colors ${
          active ? "text-sky-300" : "text-blue-300/70 group-hover:text-blue-200"
        }`}
      >
        {item.icon}
      </span>
      <span className="tracking-wide">{item.label}</span>
      {active && (
        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-sky-400 shadow-[0_0_6px_2px_rgba(56,189,248,0.5)]" />
      )}
    </Link>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const visibleNav = navItems.filter(
    (item) => !item.roles || (user && item.roles.includes(user.role))
  );

  const visibleSections = navSections
    .filter((s) => !s.roles || (user && s.roles.includes(user.role)))
    .map((s) => ({
      ...s,
      items: s.items.filter((item) => !item.roles || (user && item.roles.includes(user.role))),
    }))
    .filter((s) => s.items.length > 0);

  const initials = [user?.first_name?.[0], user?.last_name?.[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase() || user?.username?.[0]?.toUpperCase() || "U";

  const displayName = user?.first_name
    ? `${user.first_name} ${user.last_name}`.trim()
    : user?.username ?? "";

  const roleLabel: Record<string, string> = {
    super_admin: "Super Admin",
    property_manager: "Property Manager",
    tenant: "Tenant",
  };

  const sidebar = (
    <aside className="flex flex-col h-full w-64 bg-gradient-to-b from-[#0f2557] via-[#0e2050] to-[#0a1a3e] text-white select-none">

      {/* Logo */}
      <div className="flex items-center gap-3 px-5 pt-6 pb-5">
        <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-sky-500/20 border border-sky-400/30 shadow-inner">
          <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
            <path
              d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z"
              fill="url(#drop)"
              stroke="rgba(56,189,248,0.6)"
              strokeWidth="1"
            />
            <defs>
              <linearGradient id="drop" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
                <stop stopColor="#38bdf8" />
                <stop offset="1" stopColor="#0ea5e9" stopOpacity="0.7" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div>
          <p className="text-[15px] font-bold leading-tight tracking-wide text-white">SmartWater</p>
          <p className="text-[11px] text-sky-400/70 font-medium tracking-widest uppercase mt-0.5">Billing System</p>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-5 border-t border-white/8 mb-4" />

      {/* Nav links with sections */}
      <nav className="flex-1 px-3 overflow-y-auto space-y-4 pb-2">
        {visibleSections.map((section) => (
          <div key={section.label}>
            <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-[0.14em] uppercase text-blue-400/40">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.path + item.label}
                  item={item}
                  active={
                    item.path === "/"
                      ? location.pathname === "/"
                      : location.pathname.startsWith(item.path)
                  }
                  onClick={() => setSidebarOpen(false)}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="px-3 pb-4 mt-4 space-y-2">
        <div className="mx-2 border-t border-white/8 mb-3" />

        {/* User card */}
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/6 border border-white/8">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center text-[13px] font-bold text-white shadow flex-shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-white truncate leading-tight">{displayName}</p>
            <p className="text-[11px] text-blue-300/60 truncate mt-0.5">
              {roleLabel[user?.role ?? ""] ?? user?.role}
            </p>
          </div>
        </div>

        {/* Sign out */}
        <button
          onClick={handleLogout}
          className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium text-blue-300/60 hover:text-red-300 hover:bg-red-500/10 transition-all duration-150"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-[17px] h-[17px] flex-shrink-0">
            <path d="M17 16l4-4m0 0l-4-4m4 4H7" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 20H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h4" strokeLinecap="round" />
          </svg>
          <span className="tracking-wide">Sign Out</span>
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-[#f5f6fa]">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        {sidebar}
      </div>

      {/* Mobile sidebar drawer */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex lg:hidden">
            {sidebar}
          </div>
        </>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center gap-4 px-4 py-3 bg-white border-b border-gray-100 shadow-sm">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 hover:text-gray-800 transition-colors"
            aria-label="Open menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5">
              <path d="M12 2C8 7 5 10.5 5 14a7 7 0 0 0 14 0c0-3.5-3-7-7-12z" fill="#0ea5e9" />
            </svg>
            <span className="font-bold text-gray-800 text-[15px] tracking-wide">SmartWater</span>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-5 lg:p-7">
          {children}
        </main>
      </div>
    </div>
  );
}
